// The key that encrypts the cached book PDFs (bookPdfCache). AES-GCM 256, made by WebCrypto as non-extractable, so
// its bytes never reach JavaScript; the CryptoKey object itself is kept in IndexedDB (db `book-pdf-key`, store
// `keys`, key `v1`), created lazily on the first store. Sign-out deletes it, so any copy left behind is unreadable.
// Stored format: "BPC1" (4 bytes) + 12-byte random IV + GCM ciphertext (tag included). The cache entry's request
// key is the additional data, so a copy moved to another book's slot does not decrypt.

export interface KeyStore {
  get(): Promise<CryptoKey | undefined>;
  /** Stores the key unless one is there already (another tab won the race): false then. */
  add(key: CryptoKey): Promise<boolean>;
  delete(): Promise<void>;
}

const DB_NAME = "book-pdf-key";
const STORE = "keys";
const KEY_ID = "v1";
const MAGIC = [0x42, 0x50, 0x43, 0x31]; // "BPC1"
const IV_BYTES = 12;
const HEADER = MAGIC.length + IV_BYTES;

function req<T>(r: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

/** The real store, or null without IndexedDB. Each call opens, uses and closes one connection. */
export function idbKeyStore(): KeyStore | null {
  if (typeof indexedDB === "undefined" || indexedDB == null) return null;
  const withStore = async <T,>(mode: IDBTransactionMode, job: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> => {
    const openReq = indexedDB.open(DB_NAME, 1);
    openReq.onupgradeneeded = () => { openReq.result.createObjectStore(STORE); };
    const db = await req(openReq);
    try {
      const tx = db.transaction(STORE, mode);
      const done = new Promise<void>((resolve, reject) => {
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
        tx.onabort = () => reject(tx.error);
      });
      // a failed request throws below before `done` is awaited: its abort must not be an unhandled rejection
      done.catch(() => {});
      const result = await req(job(tx.objectStore(STORE)));
      await done;
      return result;
    } finally {
      db.close();
    }
  };
  return {
    get: () => withStore("readonly", (s) => s.get(KEY_ID) as IDBRequest<CryptoKey | undefined>),
    add: async (key) => {
      try {
        await withStore("readwrite", (s) => s.add(key, KEY_ID));
        return true;
      } catch (e) {
        if (e instanceof DOMException && e.name === "ConstraintError") return false;
        throw e;
      }
    },
    delete: async () => { await withStore("readwrite", (s) => s.delete(KEY_ID)); },
  };
}

let override: KeyStore | null | undefined;
let real: KeyStore | null | undefined;
/** Tests: an in-memory store (jsdom has no IndexedDB); null = no store; undefined = back to the real one. */
export function setKeyStoreForTests(store: KeyStore | null | undefined) { override = store; }
export function keyStore(): KeyStore | null {
  if (override !== undefined) return override;
  if (real === undefined) real = idbKeyStore();
  return real;
}

export const cryptoAvailable = () =>
  typeof crypto !== "undefined" && crypto?.subtle != null && typeof crypto.subtle.encrypt === "function";

/** The key, or null if there is none and `create` is false. */
export async function getKey(create: boolean): Promise<CryptoKey | null> {
  const store = keyStore();
  if (!store) return null;
  const existing = await store.get();
  if (existing || !create) return existing ?? null;
  const key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
  if (await store.add(key)) return key;
  return (await store.get()) ?? null;
}

export async function deleteKey(): Promise<void> {
  await keyStore()?.delete();
}

const aad = (label: string) => new TextEncoder().encode(label);

/** A sealed copy: its parts ("BPC1" + IV + ciphertext) as a stream for a Response body, and its stored size. */
export interface Sealed { body: ReadableStream<Uint8Array>; size: number }

/** Seals [plain]; the ciphertext is handed on as it is, never copied into a new buffer with the header. */
export async function seal(key: CryptoKey, plain: ArrayBuffer, label: string): Promise<Sealed> {
  const iv = crypto.getRandomValues(new Uint8Array(IV_BYTES));
  const ct = new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv, additionalData: aad(label) }, key, plain));
  const parts = [new Uint8Array(MAGIC), iv, ct];
  const body = new ReadableStream<Uint8Array>({
    start(c) { for (const p of parts) c.enqueue(p); c.close(); },
  });
  return { body, size: HEADER + ct.byteLength };
}

/** The plaintext, or null when the copy is not ours or was changed (bad magic, bad tag, wrong key, other slot). */
export async function unseal(key: CryptoKey, sealed: ArrayBuffer, label: string): Promise<ArrayBuffer | null> {
  if (sealed.byteLength < HEADER + 16) return null;
  const b = new Uint8Array(sealed);
  if (MAGIC.some((m, i) => b[i] !== m)) return null;
  try {
    return await crypto.subtle.decrypt(
      { name: "AES-GCM", iv: b.subarray(MAGIC.length, HEADER), additionalData: aad(label) }, key, b.subarray(HEADER));
  } catch {
    return null; // OperationError: tag mismatch
  }
}
